import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { useStoreStatus } from "../lib/storeStatus";
import Header from "../components/Header";
import { toast } from "sonner";
import { CreditCard, Landmark, PlusCircle, ShieldCheck } from "lucide-react";
import { normaliseServerId } from "../lib/serverIdFormat";

// Small helpers keep the JSX free of nested ternaries.
function packagePriceGbp(p, qty) {
  if (!p) return 0;
  if (p.product_type !== "apex") return p.price_gbp;
  const tier = p.tiers?.find(t => qty >= t.min_qty && qty <= t.max_qty);
  return (tier?.unit_price_gbp || 0) * qty;
}
function packageUnitLabel(p, qty) {
  if (!p) return { amount: 0, name: "", unit: "" };
  if (p.product_type === "apex") {
    return { amount: qty * p.apex_amount, name: `${qty} × ${p.name}`, unit: "Apex" };
  }
  return { amount: p.coin_amount, name: p.name, unit: "Empire Coins" };
}

// Rolling next-order coupon system removed per owner request (Feb 2026).

export default function Checkout() {
  const [params] = useSearchParams();
  useNavigate();  // reserved for future post-purchase redirect; keep hook mounted
  const { user, loading } = useAuth();
  const { format, code, isBase } = useCurrency();
  const { salesPaused } = useStoreStatus();
  const productType = params.get("product_type") || "apex";
  const productId = params.get("product_id") || params.get("pkg");
  const qtyParam = Math.max(1, Math.min(100, parseInt(params.get("qty") || "1", 10) || 1));
  const [pkg, setPkg] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [redemption, setRedemption] = useState(0);
  const [redemptionInput, setRedemptionInput] = useState("0");
  const [method, setMethod] = useState("card");
  const [confirmed, setConfirmed] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedImmediate, setAgreedImmediate] = useState(false);
  const [policyVersions, setPolicyVersions] = useState({ terms: null, refund: null });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newProfile, setNewProfile] = useState({ profile_name:"", server_id:"", governor_uid:"", in_game_name:"", is_default: true });
  const [showNew, setShowNew] = useState(false);
  const [region, setRegion] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoStatus, setPromoStatus] = useState(null);
  const [referralInput, setReferralInput] = useState("");
  const [referralStatus, setReferralStatus] = useState(null); // {valid, code, message}
  const [referralMeta, setReferralMeta] = useState(null); // {can_apply_referral, ...}

  useEffect(() => {
    if (!productId) return;
    if (productType === "apex") {
      api.get(`/apex-products/${productId}`).then(r => setPkg({ ...r.data, product_type: "apex" })).catch(() => {});
    } else {
      api.get(`/empire-products/${productId}`).then(r => setPkg({ ...r.data, product_type: "empire" })).catch(() => {});
    }
    if (user) {
      api.get("/game-profiles").then(r => { setProfiles(r.data); if (r.data.length) setProfileId(r.data.find(p=>p.is_default)?.id || r.data[0].id); });
      api.get("/my/referral").then(r => setReferralMeta(r.data)).catch(() => {});
    }
    api.get(`/region-hint${code !== "GBP" ? `?currency=${code}` : ""}`).then(r => setRegion(r.data)).catch(() => {});
    // Pull the live policy versions so we can stamp which version the
    // customer accepted. Falls back gracefully if the endpoint is down.
    api.get("/policies/versions").then(r => setPolicyVersions(r.data.versions || {})).catch(() => {});
  }, [productId, productType, user, code]);

  // Pay by Bank is GBP-only. If the user switches currency away from GBP
  // while Pay by Bank is selected, snap them back to card so we never
  // submit an unsupported method for the current currency.
  useEffect(() => {
    if (code !== "GBP" && method === "pay_by_bank") setMethod("card");
  }, [code, method]);

  const refreshQuote = useCallback(async () => {
    if (!pkg || !profileId) return;
    const body = {
      product_type: productType,
      product_id: pkg.id,
      quantity: qtyParam,
      package_id: pkg.id,
      game_profile_id: profileId,
      reward_redemption: redemption,
      payment_method: method,
      origin_url: window.location.origin,
      confirmed_details: true,
      promo_code: promoStatus?.valid ? promoStatus.code : null,
      referral_code: referralStatus?.valid ? referralStatus.code : null,
    };
    try {
      const { data } = await api.post("/checkout/quote", body);
      setQuote(data);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.debug("checkout quote refresh failed", err?.response?.data?.detail || err?.message);
      }
    }
  }, [pkg, profileId, redemption, method, productType, qtyParam, promoStatus, referralStatus]);

  useEffect(() => { refreshQuote(); }, [refreshQuote]);

  if (!loading && !user) return (
    <div>
      <Header/>
      <div className="max-w-md mx-auto pt-32 px-4 text-center">
        <div className="vault-card p-8">
          <h1 className="font-serif text-3xl font-bold gold-text mb-3">Unlock The Vault</h1>
          <p className="text-[var(--av-text-dim)] mb-6">Create your Arthur's Vault account to purchase Apex, earn rewards, and track your orders.</p>
          <div className="flex gap-3 justify-center">
            <Link data-testid="checkout-create-account" to={`/auth?mode=register`} className="btn-gold">Create Account</Link>
            <Link data-testid="checkout-login" to={`/auth?mode=login`} className="btn-outline-gold">Log In</Link>
          </div>
        </div>
      </div>
    </div>
  );

  const addProfile = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/game-profiles", newProfile);
    const list = await api.get("/game-profiles"); setProfiles(list.data);
    setProfileId(data.id); setShowNew(false);
    toast.success("Game profile saved");
  };

  const pay = async () => {
    // Enforce every required acknowledgement client-side FIRST. Any unticked
    // box scrolls THAT SPECIFIC checkbox into view (not just the container),
    // focuses it, and pulses the whole row so it's impossible to miss.
    const focusUnticked = () => {
      let cbSel = null, msg = "";
      if (!confirmed) {
        cbSel = '[data-testid="confirm-checkbox"]';
        msg = "Please tick the confirmation box to continue";
      } else if (!agreedTerms) {
        cbSel = '[data-testid="terms-checkbox"]';
        msg = "Please agree to the Terms & Conditions and Refund Policy";
      } else if (!agreedImmediate) {
        cbSel = '[data-testid="immediate-checkbox"]';
        msg = "Please request immediate fulfilment to continue";
      }
      const cb = cbSel && document.querySelector(cbSel);
      const row = cb && cb.closest("label");
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        row.classList.add("bg-[rgba(212,175,55,0.14)]", "ring-2", "ring-[var(--av-gold)]", "rounded-md", "px-1");
        setTimeout(() =>
          row.classList.remove("bg-[rgba(212,175,55,0.14)]", "ring-2", "ring-[var(--av-gold)]", "rounded-md", "px-1"),
          2600);
      } else {
        const card = document.getElementById("confirm-details-card");
        if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (cb) setTimeout(() => cb.focus(), 500);
      if (msg) toast.error(msg);
    };
    if (!confirmed || !agreedTerms || !agreedImmediate) {
      focusUnticked();
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/create", {
        product_type: productType, product_id: pkg.id, quantity: qtyParam,
        package_id: pkg.id, game_profile_id: profileId,
        reward_redemption: redemption, payment_method: method,
        promo_code: promoStatus?.valid ? promoStatus.code : null,
        referral_code: referralStatus?.valid ? referralStatus.code : null,
        origin_url: window.location.origin, confirmed_details: true,
        accepted_terms: true,
        accepted_refund_policy: true,
        accepted_immediate_fulfilment: true,
        accepted_terms_version: policyVersions.terms,
        accepted_refund_version: policyVersions.refund,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      // Server-side fallback: if for any reason the backend rejects with a
      // policy-acceptance error (state drift, stale render, race), route the
      // customer to the correct checkbox instead of a dead-end toast.
      const detail = String(e?.response?.data?.detail || "");
      const low = detail.toLowerCase();
      if (low.includes("terms") || low.includes("refund") || low.includes("immediate") || low.includes("fulfil")) {
        focusUnticked();
      } else {
        toast.error(detail || "Checkout failed");
      }
      setBusy(false);
    }
  };

  const displayPrice = packagePriceGbp(pkg, qtyParam);
  const { amount: displayApex, name: displayName, unit: displayUnit } = packageUnitLabel(pkg, qtyParam);
  const maxRedeem = pkg ? Math.min(user?.reward_available || 0, displayPrice * 0.05) : 0;

  const payButtonLabel = () => {
    if (salesPaused) return "Sales Paused";
    if (busy) return "Redirecting…";
    if (quote) return `Pay ${format(quote.cash_to_pay)}`;
    if (pkg) return `Pay ${format(pkg.price_gbp)}`;
    return "Pay";
  };

  return (
    <div>
      <Header/>
      <div className="max-w-4xl mx-auto pt-32 pb-16 px-4 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          {/* Package */}
          {pkg && (
            <div className="vault-card p-6">
              <div className="mono-caption">Your Order</div>
              <div className="flex justify-between items-center mt-2">
                <div>
                  <div className="font-serif text-2xl font-bold gold-text">{displayApex.toLocaleString()} {displayUnit}</div>
                  <div className="text-sm text-[var(--av-text-dim)]">{displayName}</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{format(displayPrice)}</div>
                  {!isBase && <div className="text-[10px] text-[var(--av-text-muted)]">£{displayPrice.toFixed(2)} GBP</div>}
                </div>
              </div>
            </div>
          )}

          {/* Game profile */}
          <div className="vault-card p-6">
            <div className="mono-caption mb-3">Select Your Game Profile</div>
            {profiles.length === 0 && <p className="text-sm text-[var(--av-text-dim)] mb-3">You haven't saved any Age of Empires Mobile profiles yet.</p>}
            {profiles.length > 0 && (
              <select data-testid="profile-select" value={profileId} onChange={e=>setProfileId(e.target.value)} className="input-vault mb-3">
                {profiles.map(p => <option key={p.id} value={p.id}>{p.profile_name} — Server {p.server_id} — {p.in_game_name}</option>)}
              </select>
            )}
            <button data-testid="add-profile-btn" onClick={()=>setShowNew(!showNew)} className="text-sm text-[var(--av-gold)] flex items-center gap-1"><PlusCircle className="w-4 h-4"/> Add new game profile</button>
            {showNew && (
              <form onSubmit={addProfile} className="space-y-2 mt-4">
                <input required placeholder="Profile name (e.g. Main)" className="input-vault" value={newProfile.profile_name} onChange={e=>setNewProfile({...newProfile, profile_name: e.target.value})}/>
                <div>
                  <input required placeholder="Server ID (e.g. E05)" className="input-vault font-mono uppercase"
                    value={newProfile.server_id}
                    onChange={e=>setNewProfile({...newProfile, server_id: e.target.value})}
                    onBlur={e=>setNewProfile({...newProfile, server_id: normaliseServerId(e.target.value)})}/>
                  <p className="text-[11px] text-[var(--av-text-muted)] mt-1 leading-snug">
                    Format: <span className="font-mono text-[var(--av-gold)]">E</span> + 2-digit server number. Server 5 → <span className="font-mono text-[var(--av-gold)]">E05</span>, server 10 → <span className="font-mono text-[var(--av-gold)]">E10</span>, server 175 → <span className="font-mono text-[var(--av-gold)]">E175</span>. Auto-formats when you tab away.
                  </p>
                </div>
                <div>
                  <input required placeholder="Governor UID (numbers only, no spaces)" className="input-vault" value={newProfile.governor_uid} onChange={e=>setNewProfile({...newProfile, governor_uid: e.target.value})}/>
                  <p className="text-[11px] text-[var(--av-text-muted)] mt-1 leading-snug">
                    Find it in-game: tap your avatar → <span className="text-[var(--av-gold)]">Profile</span> → the ID number under your name.
                  </p>
                </div>
                <input required placeholder="In-Game Name" className="input-vault" value={newProfile.in_game_name} onChange={e=>setNewProfile({...newProfile, in_game_name: e.target.value})}/>
                <button data-testid="save-profile-btn" className="btn-purple text-sm">Save profile</button>
              </form>
            )}
          </div>

          {/* Confirm details */}
          {profileId && (
            <div id="confirm-details-card" className="vault-card p-6 transition-shadow">
              <div className="mono-caption mb-3">Confirm Your Details</div>
              {(() => { const p = profiles.find(x => x.id === profileId); if (!p) return null; return (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mb-3">
                  <div className="min-w-0"><div className="text-[var(--av-text-muted)] text-xs">Server ID</div><div className="font-mono break-all">{p.server_id}</div></div>
                  <div className="min-w-0"><div className="text-[var(--av-text-muted)] text-xs">Governor UID</div><div className="font-mono break-all">{p.governor_uid}</div></div>
                  <div className="min-w-0"><div className="text-[var(--av-text-muted)] text-xs">In-Game Name</div><div className="font-mono break-all">{p.in_game_name}</div></div>
                </div>);})()}
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" data-testid="confirm-checkbox" checked={confirmed} onChange={e=>setConfirmed(e.target.checked)} className="mt-1"/>
                <span>I have checked that my Server ID, Governor UID and In-Game Name are correct.</span>
              </label>
              {/* Legal acknowledgements — required, not pre-ticked. Policy
                  links open in a new tab so the customer never loses their
                  checkout state. */}
              <div className="mt-3 pt-3 border-t border-[var(--av-border)]/60 space-y-2.5">
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" data-testid="terms-checkbox"
                          checked={agreedTerms}
                          onChange={e=>setAgreedTerms(e.target.checked)}
                          className="mt-1 shrink-0"/>
                  <span>
                    I agree to the{" "}
                    <a href="/legal/terms" target="_blank" rel="noopener noreferrer"
                        className="text-[var(--av-gold)] underline underline-offset-2">Terms &amp; Conditions</a>{" "}
                    and acknowledge the{" "}
                    <a href="/legal/refund" target="_blank" rel="noopener noreferrer"
                        className="text-[var(--av-gold)] underline underline-offset-2">Refund &amp; Order Issues Policy</a>.
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" data-testid="immediate-checkbox"
                          checked={agreedImmediate}
                          onChange={e=>setAgreedImmediate(e.target.checked)}
                          className="mt-1 shrink-0"/>
                  <span>
                    I request immediate fulfilment of my order and acknowledge that once supply of the digital content begins,
                    I may lose my right to cancel, subject to my statutory rights.
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Payment method */}
          <div className="vault-card p-6">
            <div className="mono-caption mb-3">Choose Payment Method</div>
            {region && region.country !== "GB" && (
              <div className="text-xs text-[var(--av-text-dim)] mb-3">
                Detected region: <span className="text-[var(--av-gold)] font-mono">{region.country}</span> · popular here:{" "}
                {region.popular_methods.map((m, i) => (
                  <span key={m.code} className="text-[var(--av-text)]">
                    {i > 0 && ", "}{m.label}
                  </span>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button data-testid="method-card" onClick={()=>setMethod("card")} className={`p-4 rounded-xl border transition text-left ${method==="card" ? "border-[var(--av-gold)] bg-[rgba(212,175,55,0.06)]" : "border-[var(--av-border)]"}`}>
                <CreditCard className="w-5 h-5 text-[var(--av-gold)] mb-2"/>
                <div className="font-semibold text-sm">Card</div>
                <div className="text-xs text-[var(--av-text-dim)]">Visa · Mastercard · Apple Pay · Onelink</div>
              </button>
              {code === "GBP" && (
                <button data-testid="method-pbb" onClick={()=>setMethod("pay_by_bank")} className={`p-4 rounded-xl border transition text-left ${method==="pay_by_bank" ? "border-[var(--av-purple)] bg-[rgba(148,0,211,0.08)]" : "border-[var(--av-border)]"}`}>
                  <Landmark className="w-5 h-5 text-[var(--av-purple)] mb-2"/>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm">Pay by Bank</div>
                      <div className="text-[10px] text-[var(--av-text-muted)] leading-snug mt-1">UK bank accounts only · Extra Apex Rewards when you Pay by Bank</div>
                    </div>
                    <span className="pill pill-purple shrink-0">+1% BONUS</span>
                  </div>
                </button>
              )}
            </div>
            <div className="text-[10px] text-[var(--av-text-muted)] mt-3">All methods charge in GBP. Buyers outside the UK may see currency conversion on their statement.</div>
          </div>
        </div>

        {/* Right column: Rewards + Promo + Summary */}
        <div className="lg:col-span-2 space-y-4">
          {/* Apex Rewards — custom amount, capped at 5% */}
          {user && user.reward_available > 0 && pkg && (
            <div className="vault-card p-6" data-testid="rewards-card">
              <div className="mono-caption mb-2">Apex Rewards</div>
              <p className="text-xs text-[var(--av-text-dim)] mb-3">
                Available: <span className="purple-text font-semibold">{format(user.reward_available)}</span>
                <span className="mx-1">·</span>
                Max on this order (5%): <span className="text-[var(--av-gold)] font-semibold">{format(maxRedeem)}</span>
                <span className="mx-1">·</span>
                Minimum redemption: <span className="text-[var(--av-gold)] font-semibold">£1.00</span>
              </p>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--av-text-dim)] text-sm">£</span>
                  <input
                    data-testid="redeem-input"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max={maxRedeem}
                    step="0.01"
                    placeholder="0.00"
                    className="input-vault pl-6"
                    value={redemptionInput}
                    onChange={e => {
                      const raw = e.target.value;
                      setRedemptionInput(raw);
                      const n = parseFloat(raw);
                      if (isNaN(n) || n < 0) setRedemption(0);
                      else setRedemption(Math.min(n, maxRedeem));
                    }}
                    onBlur={() => {
                      const n = parseFloat(redemptionInput);
                      const clamped = isNaN(n) ? 0 : Math.max(0, Math.min(n, maxRedeem));
                      setRedemption(clamped);
                      setRedemptionInput(clamped.toFixed(2));
                    }}
                  />
                </div>
                <button
                  type="button"
                  data-testid="redeem-max-btn"
                  onClick={() => { setRedemption(maxRedeem); setRedemptionInput(maxRedeem.toFixed(2)); }}
                  className="btn-outline-gold text-xs px-3"
                >Use Max</button>
              </div>
              <p className="text-[11px] text-[var(--av-text-muted)] mt-2">
                Enter any amount from £1.00 up to the cap, or leave at 0 to skip. Amounts under £1 are ignored.
              </p>
            </div>
          )}

          {/* Promo code */}
          {pkg && (
            <div className="vault-card p-6">
              <div className="mono-caption mb-2">Promo Code</div>
              <div className="flex gap-2">
                <input
                  data-testid="promo-input"
                  className="input-vault flex-1 font-mono uppercase"
                  placeholder="LAUNCH20"
                  value={promoCode}
                  onChange={e=>{ setPromoCode(e.target.value.toUpperCase().replace(/\s/g,"")); setPromoStatus(null); }}
                  disabled={!!promoStatus?.valid}
                />
                {promoStatus?.valid ? (
                  <button
                    type="button"
                    data-testid="promo-remove-btn"
                    onClick={()=>{ setPromoCode(""); setPromoStatus(null); }}
                    className="btn-outline-gold text-sm"
                  >Remove</button>
                ) : (
                  <button
                    type="button"
                    data-testid="promo-apply-btn"
                    disabled={!promoCode}
                    onClick={async ()=>{
                      try {
                        const { data } = await api.post("/promo-codes/validate", {
                          code: promoCode,
                          package_price_gbp: pkg.price_gbp || pkg.total_price_gbp || 0,
                          product_type: productType,
                        });
                        setPromoStatus(data);
                        if (data.valid) toast.success(`Promo applied: ${data.code}`);
                        else toast.error(data.message || "Invalid code");
                      } catch(err){ toast.error(err?.response?.data?.detail || "Failed"); }
                    }}
                    className="btn-gold text-sm"
                  >Apply</button>
                )}
              </div>
              {promoStatus?.valid && (
                <div className="text-xs text-[var(--av-gold)] mt-2" data-testid="promo-applied">
                  ✓ {promoStatus.code} — {promoStatus.discount_type==="percentage" ? `${promoStatus.discount_value}% off` : `£${Number(promoStatus.discount_value).toFixed(2)} off`}
                </div>
              )}
              {promoStatus && !promoStatus.valid && (
                <div className="text-xs text-[var(--av-purple)] mt-2">{promoStatus.message}</div>
              )}
            </div>
          )}

          {/* Referral code — only for eligible new customers */}
          {pkg && referralMeta?.can_apply_referral && (
            <div className="vault-card p-6" data-testid="referral-card">
              <div className="mono-caption mb-1">Referral Code</div>
              <p className="text-xs text-[var(--av-text-dim)] mb-3">
                New to Arthur's Vault? Enter a friend's code to earn an extra{" "}
                <span className="purple-text font-semibold">+{referralMeta.new_customer_bonus_percent}%</span> Apex Rewards on this qualifying £{referralMeta.min_qualifying_gbp?.toFixed(2)}+ order.
              </p>
              <div className="flex gap-2">
                <input
                  data-testid="referral-input"
                  className="input-vault flex-1 font-mono uppercase"
                  placeholder="AV-XXXXXX"
                  value={referralInput}
                  onChange={e => { setReferralInput(e.target.value.toUpperCase().replace(/\s/g, "")); setReferralStatus(null); }}
                  disabled={!!referralStatus?.valid}
                />
                {referralStatus?.valid ? (
                  <button
                    type="button"
                    data-testid="referral-remove-btn"
                    onClick={() => { setReferralInput(""); setReferralStatus(null); }}
                    className="btn-outline-gold text-sm"
                  >Remove</button>
                ) : (
                  <button
                    type="button"
                    data-testid="referral-apply-btn"
                    disabled={!referralInput}
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/checkout/apply-referral", {
                          code: referralInput, game_profile_id: profileId,
                        });
                        setReferralStatus(data);
                        toast.success(data.message || "Referral applied");
                      } catch (err) {
                        setReferralStatus({ valid: false, message: err?.response?.data?.detail });
                        toast.error(err?.response?.data?.detail || "Invalid referral code");
                      }
                    }}
                    className="btn-gold text-sm"
                  >Apply</button>
                )}
              </div>
              {referralStatus?.valid && (
                <div className="text-xs text-[var(--av-gold)] mt-2" data-testid="referral-applied">
                  ✓ {referralStatus.code} — you'll earn +{referralMeta.new_customer_bonus_percent}% on delivery
                </div>
              )}
              {referralStatus && !referralStatus.valid && (
                <div className="text-xs text-[var(--av-purple)] mt-2">{referralStatus.message}</div>
              )}
            </div>
          )}

          {/* Summary */}
          <div className="vault-card p-6 lg:sticky lg:top-24">
            <div className="mono-caption mb-3">Summary</div>
            {quote && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[var(--av-text-dim)]">Package</span><span>{format(quote.package_price)}</span></div>
                <div className="flex justify-between"><span className="text-[var(--av-text-dim)]">Rewards used</span><span className="purple-text">−{format(quote.reward_redemption)}</span></div>
                {quote.promo_discount > 0 && (
                  <div className="flex justify-between"><span className="text-[var(--av-text-dim)]">Promo {quote.promo?.code || ""}</span><span className="gold-text">−{format(quote.promo_discount)}</span></div>
                )}
                <div className="flex justify-between text-xs pt-1"><span className="text-[var(--av-text-muted)]">Qualifying Amount</span><span className="text-[var(--av-text-muted)]">{format(quote.qualifying_amount || quote.cash_to_pay)}</span></div>
                {(quote.vault_offers && quote.vault_offers.length > 0
                  ? quote.vault_offers
                  : (quote.vault_offer ? [quote.vault_offer] : [])
                ).map((vo, i) => (
                  <div key={vo.id || i} className="flex justify-between text-xs" data-testid={i === 0 ? "vault-offer-applied" : `vault-offer-applied-${i}`}>
                    <span className="purple-text">✦ {vo.name}</span>
                    <span className="purple-text font-semibold">
                      {vo.reward_type === "percentage"
                        ? `+${vo.reward_percentage}%`
                        : `+${format(vo.reward_fixed_gbp)}`}
                    </span>
                  </div>
                ))}
                {quote.referral_applied && (
                  <div className="flex justify-between text-xs" data-testid="referral-summary">
                    <span className="purple-text">✦ Referral bonus</span>
                    <span className="purple-text font-semibold">+{quote.referral_bonus_rate}%</span>
                  </div>
                )}
                <div className="divider-gold my-2"></div>
                <div className="flex justify-between text-lg"><span>Total to Pay</span><span className="gold-text font-bold">{format(quote.cash_to_pay)}</span></div>
                {!isBase && <div className="text-[11px] text-[var(--av-text-muted)] text-right">£{quote.cash_to_pay.toFixed(2)} GBP charged · your bank's FX rate sets the exact debit</div>}
                <div className="text-xs text-[var(--av-text-dim)] mt-2">Reward rate: {quote.effective_rate}% · Earn <span className="purple-text">{format(quote.rewards_earned)}</span> Apex Rewards</div>
              </div>
            )}
            <button disabled={busy || !profileId || salesPaused} data-testid="pay-btn" onClick={pay} className="btn-gold w-full mt-6 disabled:opacity-40 disabled:cursor-not-allowed">{payButtonLabel()}</button>
            <div className="flex items-center justify-center gap-1 text-xs text-[var(--av-text-dim)] mt-3"><ShieldCheck className="w-3 h-3"/> Secure Stripe checkout · GBP</div>
          </div>
        </div>
      </div>
    </div>
  );
}
